(function () {
  'use strict';
  if (customElements.get('ticker-bar')) return;
  class TickerBar extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.frame = 0;
      this.playState = null;
      this.scale = this.querySelector('[data-ticker-scale]');
      this.text = this.querySelector('[data-ticker-text]');
      if (!this.scale || !this.text) return;
      this.autoplay = this.hasAttribute('data-autoplay');
      this.comparitor = this.text.cloneNode(true);
      this.comparitor.classList.add('ticker__comparitor');
      this.comparitor.classList.remove('text-highlight-wrapper');
      this.comparitor.setAttribute('aria-hidden', 'true');
      this.comparitor.inert = true;
      this.appendChild(this.comparitor);
      this.scale.classList.remove('ticker--unloaded');
      this.resizeEvent = () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => this.schedule(), 120); };
      this.onVisibility = () => this.pause();
      this.onFocus = () => { this.focused = true; this.pause(); };
      this.onBlur = event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.pause(); } };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', this.onVisibility);
      window.addEventListener('resize', this.resizeEvent);
      window.addEventListener('orientationchange', this.resizeEvent);
      document.addEventListener('theme:resize:width', this.resizeEvent);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.addEventListener('focusin', this.onFocus);
      this.addEventListener('focusout', this.onBlur);
      this.editorObserver = new MutationObserver(() => {
        if (this.scale.hasAttribute('data-stop')) {
          this.animation?.cancel(); this.animation = null; this.playState = null;
        } else { this.schedule(); }
        this.pause();
      });
      this.editorObserver.observe(this.scale, {attributes: true, attributeFilter: ['data-stop']});
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries.some(entry => entry.isIntersecting);
          if (this.inView) this.schedule();
          this.pause();
        }, {rootMargin: '350px 0px'});
        this.observer.observe(this);
      } else { this.inView = true; this.schedule(); }
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(); });
    }
    schedule() {
      if (!this.inView || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected && this.inView) this.checkWidth(); });
    }
    checkWidth() {
      const style = getComputedStyle(this);
      const available = this.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
      const measured = this.comparitor.clientWidth || this.text.clientWidth;
      if (available <= 0 || measured <= 0) return;
      const animated = this.autoplay || available < measured;
      const wanted = animated ? (this.autoplay ? Math.max(Math.ceil(2 * available / measured), 2) + 1 : 3) : 0;
      const copiesChanged = this.copyCount !== wanted;
      if (copiesChanged) {
        this.scale.querySelectorAll('[data-clone]').forEach(node => node.remove());
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < wanted; i++) {
          const copy = this.text.cloneNode(true);
          copy.setAttribute('data-clone', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          copy.removeAttribute('id');
          copy.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
          fragment.appendChild(copy);
        }
        this.scale.appendChild(fragment);
        this.copyCount = wanted;
        this.playState = null;
        this.scale.querySelectorAll('[data-ticker-text]').forEach(node => node.classList.toggle('ticker--animated', animated));
        if (this.autoplay) this.querySelectorAll('[data-aos]').forEach(node => node.classList.add('aos-animate'));
      }
      const speed = Math.max(1, parseInt(this.getAttribute('data-marquee-speed'), 10) || 100);
      const duration = measured / 100 * 1.63 * (100 / speed);
      if (duration !== this.duration) { this.scale.style.setProperty('--animation-time', duration + 's'); this.duration = duration; }
      if (this.autoplay) this.syncTrack(speed);
      this.pause();
    }
    syncTrack(speed) {
      this.scale.style.display = 'inline-flex';
      this.scale.style.width = 'max-content';
      this.scale.querySelectorAll('[data-ticker-text]').forEach(node => {
        node.style.animationName = 'none';
        node.style.willChange = 'auto';
        node.style.flexShrink = '0';
      });
      if (this.scale.hasAttribute('data-stop')) return;
      const width = this.text.getBoundingClientRect().width;
      if (width <= 0) return;
      const duration = width / 100 * 1630 * (100 / speed);
      const direction = this.text.classList.contains('ticker-ltr') ? 'reverse' : 'normal';
      if (this.animation && width === this.trackWidth && duration === this.trackDuration && direction === this.trackDirection) return;
      const progress = this.animation && this.trackDuration ? (Number(this.animation.currentTime) || 0) % this.trackDuration / this.trackDuration : 0;
      this.animation?.cancel();
      this.scale.style.willChange = 'transform';
      this.animation = this.scale.animate(
        [{transform: 'translate3d(0, 0, 0)'}, {transform: `translate3d(${-width}px, 0, 0)`}],
        {duration, direction, iterations: Infinity, easing: 'linear'}
      );
      this.animation.currentTime = progress * duration;
      this.trackWidth = width; this.trackDuration = duration; this.trackDirection = direction;
      this.playState = null;
    }
    pause() {
      const state = !this.inView || document.hidden || this.focused || this.scale?.hasAttribute('data-stop') || this.motion.matches ? 'paused' : 'running';
      if (state === this.playState) return;
      this.playState = state;
      if (this.animation) { state === 'paused' ? this.animation.pause() : this.animation.play(); }
      this.scale?.querySelectorAll('[data-ticker-text]').forEach(node => { node.style.animationPlayState = state; });
    }
    disconnectedCallback() {
      this.observer?.disconnect(); clearTimeout(this.resizeTimer); cancelAnimationFrame(this.frame);
      this.editorObserver?.disconnect(); this.animation?.cancel(); this.animation = null;
      this.motion?.removeEventListener('change', this.onVisibility);
      window.removeEventListener('resize', this.resizeEvent);
      window.removeEventListener('orientationchange', this.resizeEvent);
      document.removeEventListener('theme:resize:width', this.resizeEvent);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.removeEventListener('focusin', this.onFocus); this.removeEventListener('focusout', this.onBlur);
      this.comparitor?.remove(); this.ready = false;
    }
  }
  customElements.define('ticker-bar', TickerBar);
})();
